<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaCountLabel from '../base/GaiaCountLabel.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Panel title, e.g. "Extracted fields", "Results". */
  title: {
    type: String,
    required: true,
  },
  /** Muted count beside the title. */
  count: {
    type: [String, Number] as PropType<string | number>,
    default: undefined,
  },
});

const border = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
</script>

<template>
  <!-- The bordered list panel from the review detail (Kanban mock): a titled
       header with #actions on the right, over a list of rows in the default
       slot. A GaiaSectionHeader strip inside it rules off a group of those
       rows, so it has to run to the panel's edges — the body's gutter is what
       the strip cancels, which is why the panel owns both numbers. -->
  <div class="gaia-results-panel">
    <n-flex align="center" :size="9" :wrap="false" class="head">
      <gaia-text size="body" weight="semibold" tone="ink">{{ props.title }}</gaia-text>
      <gaia-count-label v-if="props.count !== undefined">{{ props.count }}</gaia-count-label>
      <n-flex
        v-if="$slots.actions"
        align="center"
        :size="8"
        :wrap="false"
        :style="{ marginLeft: 'auto', flex: 'none' }"
      >
        <slot name="actions" />
      </n-flex>
    </n-flex>
    <div class="body"><slot /></div>
  </div>
</template>

<style scoped>
.gaia-results-panel {
  border: 1px solid v-bind(border);
  border-radius: 12px;
  background: v-bind(surface);
  /* The strips below reach the panel's edges, so they have to be clipped by
     its radius rather than squaring off its corners. */
  overflow: hidden;
}

.head {
  padding: 13px 16px;
}

.body {
  padding: 0 16px 12px;
}

/* Cancels `.body`'s gutter so the strip spans the panel. Its own top hairline
   is what separates it from the header and from the rows above it. */
.body :deep(.gaia-section-header--strip) {
  margin: 0 -16px;
}

/* A collapse row carries its spacing on top only, so the last row of a group
   would otherwise sit flush against the next group's band. The first strip
   keeps its hairline against the panel header, where that is the wanted join. */
.body :deep(.gaia-section-header--strip:not(:first-child)) {
  margin-top: 12px;
}

/* naive drops the leading margin and divider on the collapse's own first row,
   but here the collapse's first child is a group strip, so the row under every
   strip keeps a 16px margin and a hairline the strip has already drawn. Taking
   both back leaves the row's own 12px header padding — the same gap the strip
   above gets from the rule before this one, so a group is inset equally at both
   ends. */
.body :deep(.gaia-section-header--strip + .n-collapse-item) {
  margin-top: 0;
  border-top: none;
}

/* A collapse header is a flex row whose main cell sizes to its content, which
   pushes #header-extra's chips off the row as soon as a title is long. Rows in
   this panel are the accordion's, so the fix belongs to the panel. */
.body :deep(.n-collapse-item__header-main) {
  min-width: 0;
}
</style>
